import { Checkbox, Loader, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconBox,
    IconCircleCheck,
    IconDots,
    IconRotateClockwise,
    IconTrash,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Backup, BackupSetting } from '../../../../../@types/backup';
import { Button, ConfirmActionModal } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useDeleteBackupMutate } from '../../../../../hooks/CloudComputeHooks/useDeleteBackupMutate';
import { useForceBackupMutate } from '../../../../../hooks/CloudComputeHooks/useForceBackupMutate';
import { useRestoreBackupMutate } from '../../../../../hooks/CloudComputeHooks/useRestoreBackupMutate';
import { useBackupsInfiniteQuery } from '../hooks/useBackupsInfiniteQuery';
import { formatBackupCreated, frequencyLabelKey } from './backupFormat';

interface BackupListCardProps {
    instanceId: number;
    settings?: BackupSetting;
    /** False with view-only access: backups are listed without selection, restore or delete. */
    canManage: boolean;
}

const GRID = 'grid grid-cols-[40px_24px_1fr_100px_140px_180px_140px_40px] gap-3 items-center';
// Floor for the scroll container: fixed tracks (664) + gaps (7x12) + px-4 (32) + a 200px floor
// for the name track. Keep in sync with GRID.
const GRID_MIN_W = 'min-w-[980px]';

function StatusIcon({ status }: { status: Backup['status'] }) {
    if (status === 'pending') {
        return (
            <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#f59e0b26' }}
            >
                <Loader size={14} color="#f59e0b" />
            </div>
        );
    }
    if (status === 'deleting') {
        return (
            <div
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#ef444426' }}
            >
                <Loader size={14} color="#ef4444" />
            </div>
        );
    }
    return (
        <div
            className="w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#10b98126' }}
        >
            <IconCircleCheck size={16} color="#10b981" />
        </div>
    );
}

function ActorPill({ backup, settings }: { backup: Backup; settings?: BackupSetting }) {
    const { t } = useTranslation('cloudComputeInstance');
    if (backup.action === 'automated') {
        return (
            <span
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono"
                style={{ backgroundColor: '#3b82f626', color: 'var(--color-status-info)' }}
            >
                {t(`backup.frequency.${frequencyLabelKey(settings?.frequency)}`)}
            </span>
        );
    }
    return <span className="text-sm text-fg/80">{t('backup.frequency.manual')}</span>;
}

function shortId(id: number): string {
    return `bk-${String(id).padStart(2, '0')}`;
}

export default function BackupListCard({ instanceId, settings, canManage }: BackupListCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
        useBackupsInfiniteQuery(instanceId);

    const forceMutation = useForceBackupMutate();
    const restoreMutation = useRestoreBackupMutate();
    const deleteMutation = useDeleteBackupMutate();

    const items = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data]);

    const total = data?.pages[0]?.total_rows ?? items.length;
    const totalSizeGB = items.reduce((acc, b) => acc + (b.size ?? 0), 0);
    const totalMonthly = items.reduce((acc, b) => acc + (b.monthly_cost ?? 0), 0);

    const [selected, setSelected] = useState<number[]>([]);
    const successItems = items.filter((b) => b.status === 'success');
    const allSuccessSelected =
        successItems.length > 0 && successItems.every((b) => selected.includes(b.id));

    const toggleAll = () => {
        if (allSuccessSelected) {
            setSelected([]);
        } else {
            setSelected(successItems.map((b) => b.id));
        }
    };
    const toggleOne = (id: number) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
    const [restoreOpen, { open: openRestore, close: closeRestore }] = useDisclosure();
    const [deleteTargets, setDeleteTargets] = useState<number[]>([]);
    const [deleteOpen, { open: openDelete, close: closeDelete }] = useDisclosure();

    const askRestore = (backupId: number) => {
        setRestoreTarget(backupId);
        openRestore();
    };
    const askDelete = (ids: number[]) => {
        setDeleteTargets(ids);
        openDelete();
    };

    const confirmRestore = () => {
        if (!restoreTarget) return;
        restoreMutation.mutate(
            { id: instanceId, backupId: restoreTarget },
            {
                onSuccess: () => {
                    closeRestore();
                    setRestoreTarget(null);
                    setSelected([]);
                },
            }
        );
    };

    const confirmDelete = () => {
        if (deleteTargets.length === 0) return;
        deleteMutation.mutate(
            { id: instanceId, backup_ids: deleteTargets },
            {
                onSuccess: () => {
                    closeDelete();
                    setDeleteTargets([]);
                    setSelected([]);
                },
            }
        );
    };

    const handleBackupNow = () => forceMutation.mutate(instanceId);

    const canRestoreSelected =
        selected.length === 1 && items.find((b) => b.id === selected[0])?.status === 'success';
    const canDeleteSelected =
        selected.length > 0 &&
        selected.every((id) => items.find((b) => b.id === id)?.status === 'success');

    const subtitle = t('backup.subtitle', {
        total,
        sizeGb: totalSizeGB.toFixed(0),
        monthly: totalMonthly.toFixed(2),
    });

    const listActions = (
        <div className="flex flex-row items-center gap-2">
            <Button
                size="sm"
                variant="secondary"
                leftIcon={<IconRotateClockwise size={16} />}
                onClick={() => selected[0] && askRestore(selected[0])}
                disabled={!canRestoreSelected || restoreMutation.isPending}
            >
                {t('backup.restore')}
            </Button>
            <Button
                size="sm"
                variant="danger"
                leftIcon={<IconTrash size={16} />}
                onClick={() => askDelete(selected)}
                disabled={!canDeleteSelected || deleteMutation.isPending}
            >
                {t('backup.delete')}
            </Button>
            <Button
                size="sm"
                variant="primary"
                leftIcon={<IconBox size={16} />}
                onClick={handleBackupNow}
                disabled={forceMutation.isPending}
            >
                {t('backup.backupNow')}
            </Button>
        </div>
    );

    return (
        <DialogBox
            label={t('backup.allBackupsLabel')}
            description={subtitle}
            rightDescription={canManage && listActions}
            classNames=""
        >
            <div
                className="w-full overflow-x-auto"
                tabIndex={0}
                role="region"
                aria-label={t('backup.allBackupsLabel')}
            >
                <div className={GRID_MIN_W}>
                    <div
                        className={`${GRID} w-full px-4 py-3 text-xs text-fg/68 uppercase tracking-wider text-nowrap border-b-[0.5px] border-line`}
                    >
                        {!canManage ? (
                            <span />
                        ) : (
                            <Checkbox
                                size="xs"
                                checked={allSuccessSelected}
                                indeterminate={selected.length > 0 && !allSuccessSelected}
                                onChange={toggleAll}
                                disabled={successItems.length === 0}
                                aria-label={t('backup.selectAll')}
                            />
                        )}
                        <span />
                        <span>{t('backup.headers.name')}</span>
                        <span>{t('backup.headers.size')}</span>
                        <span>{t('backup.headers.costPerMonth')}</span>
                        <span>{t('backup.headers.created')}</span>
                        <span>{t('backup.headers.createdBy')}</span>
                        <span />
                    </div>

                    {items.map((b) => {
                        const isSuccess = b.status === 'success';
                        const checked = selected.includes(b.id);
                        return (
                            <div
                                key={b.id}
                                className={`${GRID} w-full px-4 py-3 border-t-[0.5px] border-line`}
                            >
                                {!canManage ? (
                                    <span />
                                ) : (
                                    <Checkbox
                                        size="xs"
                                        checked={checked}
                                        onChange={() => toggleOne(b.id)}
                                        disabled={!isSuccess}
                                        aria-label={t('backup.selectBackup', { name: b.name })}
                                    />
                                )}
                                <StatusIcon status={b.status} />
                                <div className="flex flex-col min-w-0">
                                    <div className="text-[15px] text-fg truncate">{b.name}</div>
                                    <div className="text-xs text-fg/68 font-mono">
                                        {shortId(b.id)}
                                    </div>
                                </div>
                                <div className="text-sm text-fg">
                                    {b.size > 0 ? `${b.size.toFixed(0)} GB` : '—'}
                                </div>
                                <div className="text-sm text-fg">
                                    {b.monthly_cost > 0 ? `฿${b.monthly_cost.toFixed(2)}` : '—'}
                                </div>
                                <div className="text-sm text-fg/80">
                                    {formatBackupCreated(b.created_at)}
                                </div>
                                <div>
                                    <ActorPill backup={b} settings={settings} />
                                </div>
                                {!canManage ? (
                                    <span />
                                ) : (
                                    <Menu position="bottom-end" withinPortal>
                                        <Menu.Target>
                                            <button
                                                type="button"
                                                aria-label={t('backup.rowActions')}
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg/68 hover:bg-surface-3 hover:text-fg transition-colors"
                                            >
                                                <IconDots size={16} />
                                            </button>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                leftSection={<IconRotateClockwise size={14} />}
                                                disabled={!isSuccess}
                                                onClick={() => askRestore(b.id)}
                                            >
                                                {t('backup.actionRestore')}
                                            </Menu.Item>
                                            <Menu.Item
                                                color="var(--color-status-danger)"
                                                leftSection={<IconTrash size={14} />}
                                                disabled={!isSuccess}
                                                onClick={() => askDelete([b.id])}
                                            >
                                                {t('backup.actionDelete')}
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {isPending && !data ? (
                <div className="w-full p-8 flex justify-center">
                    <Loader size={20} color="var(--fg)" />
                </div>
            ) : items.length === 0 ? (
                <div className="w-full p-8 text-sm text-fg/68 text-center">
                    <Trans
                        i18nKey="backup.noBackupsEmpty"
                        ns="cloudComputeInstance"
                        components={{ b: <b className="text-fg" /> }}
                    />
                </div>
            ) : null}

            {hasNextPage && (
                <div className="w-full flex justify-center px-4 py-3 border-t-[0.5px] border-line">
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                    >
                        {isFetchingNextPage ? t('backup.loading') : t('backup.loadMore')}
                    </Button>
                </div>
            )}

            <ConfirmActionModal
                opened={restoreOpen}
                onClose={closeRestore}
                title={t('backup.restorePrompt')}
                message={t('backup.restoreWarning')}
                confirmLabel={t('backup.restoreNow')}
                confirmVariant="primary"
                isLoading={restoreMutation.isPending}
                onConfirm={confirmRestore}
            />
            <ConfirmActionModal
                opened={deleteOpen}
                onClose={closeDelete}
                title={
                    deleteTargets.length > 1
                        ? t('backup.deletePromptPlural')
                        : t('backup.deletePrompt')
                }
                message={
                    deleteTargets.length > 1
                        ? t('backup.deleteWarningPlural', { count: deleteTargets.length })
                        : t('backup.deleteWarning')
                }
                confirmLabel={
                    deleteTargets.length > 1
                        ? t('backup.deleteConfirmPlural')
                        : t('backup.deleteConfirm')
                }
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </DialogBox>
    );
}
